import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { toast } from "sonner";
import {
  CalendarDays, ChevronLeft, ChevronRight, Loader2, Plus, RefreshCw,
  AlertTriangle, User, Clock, X, Inbox,
} from "lucide-react";
import { listServiceJobs, patchServiceJob, createServiceJob, listAppointments, patchAppointment, fetchRoster } from "@/lib/dashboardApi";
const DAY_START = 7 * 60;
const DAY_END = 19 * 60;
const SLOT_MIN = 30;
const SLOT_PX = 92;
const ROW_H = 74;
const SLOTS = Array.from({ length: (DAY_END - DAY_START) / SLOT_MIN }, (_, i) => DAY_START + i * SLOT_MIN);
const TRACK_W = SLOTS.length * SLOT_PX;
const DEFAULT_DURATION = 60;
const CLOSED = new Set(["CLOSED", "PAID", "CANCELED"]);
const pad = (n) => String(n).padStart(2, "0");
const toISODate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const parseTime = (t) => {
  if (!t) return null;
  const s = String(t).trim();
  let m = s.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)?$/i);
  if (!m) return null;
  let h = parseInt(m[1], 10);
  const min = parseInt(m[2], 10);
  const ap = (m[3] || "").toUpperCase();
  if (ap === "PM" && h < 12) h += 12;
  if (ap === "AM" && h === 12) h = 0;
  return h * 60 + min;
};
const minToHHMM = (min) => `${pad(Math.floor(min / 60))}:${pad(min % 60)}`;
const minToLabel = (min) => {
  let h = Math.floor(min / 60);
  const m = min % 60;
  const ap = h >= 12 ? "PM" : "AM";
  h = h % 12; if (h === 0) h = 12;
  return `${h}${m ? ":" + pad(m) : ""} ${ap}`;
};
const PRIORITY_COLOR = { Emergency: "#ef4444", High: "#F97316", Standard: "#0ea5e9", Low: "#64748b" };
const jobColor = (job) => PRIORITY_COLOR[job.priority] || "#0ea5e9";

const DispatchBoard = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [view, setView] = useState("day");
  const [date, setDate] = useState(() => new Date());
  const [dragId, setDragId] = useState(null);
  const [createSlot, setCreateSlot] = useState(null);
  const [techRows, setTechRows] = useState([]);
  const trackRefs = useRef({});
  useEffect(() => { fetchRoster().then((r) => setTechRows(r.dispatch || [])).catch(() => {}); }, []);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [svc, appt] = await Promise.all([listServiceJobs(), listAppointments()]);
      const jobsT = svc.map((j) => ({ ...j, _kind: "job" }));
      const apptT = appt.map((a) => ({
        ...a, _kind: "appt", customer_name: a.customer_name, complaint: a.scope || "",
        job_type: "Appointment", priority: a.priority || "Standard", status: "", job_number: "Appointment",
      }));
      setJobs([...jobsT, ...apptT]);
    } catch { toast.error("Could not load jobs."); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const dateISO = toISODate(date);
  const weekDays = useMemo(() => {
    const start = new Date(date); start.setDate(date.getDate() - date.getDay());
    return Array.from({ length: 7 }, (_, i) => { const d = new Date(start); d.setDate(start.getDate() + i); return d; });
  }, [date]);
  const active = useMemo(() => jobs.filter((j) => !CLOSED.has(j.status)), [jobs]);
  const isRow = (t) => techRows.includes((t || "").trim());
  const placedOn = (job, iso) => isRow(job.assigned_tech) && parseTime(job.scheduled_time) != null && job.scheduled_date === iso;
  const unassigned = useMemo(() => {
    if (view === "day") return active.filter((j) => !placedOn(j, dateISO));
    const isoSet = new Set(weekDays.map(toISODate));
    return active.filter((j) => !(isRow(j.assigned_tech) && parseTime(j.scheduled_time) != null && isoSet.has(j.scheduled_date)));
  }, [active, view, dateISO, weekDays, techRows]);
  const dayJobsByTech = useMemo(() => {
    const map = {};
    techRows.forEach((t) => (map[t] = []));
    active.forEach((j) => { if (placedOn(j, dateISO) && map[j.assigned_tech.trim()]) map[j.assigned_tech.trim()].push(j); });
    Object.values(map).forEach((arr) => arr.sort((a, b) => parseTime(a.scheduled_time) - parseTime(b.scheduled_time)));
    return map;
  }, [active, dateISO, techRows]);
  const overlaps = useMemo(() => {
    const set = new Set();
    Object.values(dayJobsByTech).forEach((arr) => {
      for (let i = 1; i < arr.length; i++) {
        const prev = arr[i - 1], cur = arr[i];
        const prevEnd = parseTime(prev.scheduled_time) + (prev.duration_min || DEFAULT_DURATION);
        if (parseTime(cur.scheduled_time) < prevEnd) { set.add(prev.id); set.add(cur.id); }
      }
    });
    return set;
  }, [dayJobsByTech]);
  const applyPatch = async (jobId, patch) => {
    const item = jobs.find((j) => j.id === jobId);
    const kind = item?._kind || "job";
    setJobs((prev) => prev.map((j) => (j.id === jobId ? { ...j, ...patch } : j)));
    setSaving(true);
    try {
      let updated;
      if (kind === "appt") {
        updated = await patchAppointment(jobId, patch);
        updated = { ...updated, _kind: "appt", complaint: updated.scope || "", job_type: "Appointment", priority: updated.priority || "Standard", status: "", job_number: "Appointment" };
      } else {
        updated = await patchServiceJob(jobId, { ...patch, actor_role: "DISPATCHER", actor_name: "Dispatch Board" });
        updated = { ...updated, _kind: "job" };
      }
      setJobs((prev) => prev.map((j) => (j.id === jobId ? updated : j)));
    } catch { toast.error("Could not save change."); load(); }
    finally { setSaving(false); }
  };
  const onDragStart = (e, jobId) => { setDragId(jobId); e.dataTransfer.effectAllowed = "move"; try { e.dataTransfer.setData("text/plain", jobId); } catch {} };
  const onDragEnd = () => setDragId(null);
  const dropOnDayTrack = (e, tech) => {
    e.preventDefault();
    const id = dragId || e.dataTransfer.getData("text/plain");
    if (!id) return;
    const rect = trackRefs.current[tech]?.getBoundingClientRect();
    let startMin = DAY_START;
    if (rect) {
      const x = e.clientX - rect.left;
      const slot = Math.max(0, Math.min(SLOTS.length - 1, Math.floor(x / SLOT_PX)));
      startMin = DAY_START + slot * SLOT_MIN;
    }
    const job = jobs.find((j) => j.id === id);
    applyPatch(id, { assigned_tech: tech, scheduled_date: dateISO, scheduled_time: minToHHMM(startMin), duration_min: job?.duration_min || DEFAULT_DURATION });
    setDragId(null);
  };
  const dropOnWeekCell = (e, tech, iso) => {
    e.preventDefault();
    const id = dragId || e.dataTransfer.getData("text/plain");
    if (!id) return;
    const job = jobs.find((j) => j.id === id);
    applyPatch(id, { assigned_tech: tech, scheduled_date: iso, scheduled_time: parseTime(job?.scheduled_time) != null ? job.scheduled_time : "09:00", duration_min: job?.duration_min || DEFAULT_DURATION });
    setDragId(null);
  };
  const dropOnTray = (e) => {
    e.preventDefault();
    const id = dragId || e.dataTransfer.getData("text/plain");
    if (!id) return;
    applyPatch(id, { assigned_tech: "", scheduled_time: "" });
    setDragId(null);
  };
  const shiftDate = (n) => { const d = new Date(date); d.setDate(date.getDate() + (view === "week" ? n * 7 : n)); setDate(d); };
  const segCls = (on) => `px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${on ? "bg-[#12355B] text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-slate-300"}`;
  return (
    <div className="space-y-5" data-testid="dispatch-board">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <button onClick={() => setView("day")} className={segCls(view === "day")} data-testid="dispatch-view-day">Daily</button>
          <button onClick={() => setView("week")} className={segCls(view === "week")} data-testid="dispatch-view-week">Weekly</button>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => shiftDate(-1)} className="p-2 rounded-lg border border-slate-200 bg-white hover:border-slate-300" data-testid="dispatch-prev"><ChevronLeft className="w-4 h-4" /></button>
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 bg-white min-w-[220px] justify-center">
            <CalendarDays className="w-4 h-4 text-[#F97316]" />
            <span className="text-sm font-semibold text-slate-800" data-testid="dispatch-date-label">
              {view === "week"
                ? `${weekDays[0].toLocaleDateString("en-US", { month: "short", day: "numeric" })} – ${weekDays[6].toLocaleDateString("en-US", { month: "short", day: "numeric" })}`
                : date.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}
            </span>
          </div>
          <button onClick={() => shiftDate(1)} className="p-2 rounded-lg border border-slate-200 bg-white hover:border-slate-300" data-testid="dispatch-next"><ChevronRight className="w-4 h-4" /></button>
          <button onClick={() => setDate(new Date())} className="px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm font-semibold text-slate-600 hover:border-slate-300" data-testid="dispatch-today">Today</button>
          <input type="date" value={dateISO} onChange={(e) => e.target.value && setDate(new Date(e.target.value + "T12:00:00"))} className="px-2 py-2 rounded-lg border border-slate-200 bg-white text-sm text-slate-600" data-testid="dispatch-date-input" />
          <button onClick={load} className="p-2 rounded-lg border border-slate-200 bg-white hover:border-slate-300" title="Refresh" data-testid="dispatch-refresh">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          </button>
        </div>
      </div>
      {overlaps.size > 0 && view === "day" && (
        <div className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm font-medium" data-testid="dispatch-overlap-warning">
          <AlertTriangle className="w-4 h-4 shrink-0" /> Overlapping appointments detected — some technicians are double-booked.
        </div>
      )}
      <div className="bg-white rounded-xl border border-slate-200 p-4" onDragOver={(e) => e.preventDefault()} onDrop={dropOnTray} data-testid="dispatch-unassigned-tray">
        <div className="flex items-center gap-2 mb-3">
          <Inbox className="w-4 h-4 text-slate-500" />
          <h3 className="text-sm font-bold text-slate-700">Unassigned / Unscheduled</h3>
          <span className="text-xs font-semibold text-slate-400">({unassigned.length})</span>
          <span className="text-xs text-slate-400 ml-auto hidden sm:inline">Drag a card onto a technician row to schedule it</span>
        </div>
        {unassigned.length === 0 ? (
          <p className="text-sm text-slate-400 py-2">Nothing waiting — every job is scheduled.</p>
        ) : (
          <div className="flex gap-2 flex-wrap">
            {unassigned.map((job) => (
              <div key={job.id} draggable onDragStart={(e) => onDragStart(e, job.id)} onDragEnd={onDragEnd}
                className="cursor-grab active:cursor-grabbing rounded-lg border-l-4 bg-slate-50 border border-slate-200 px-3 py-2 w-56 hover:shadow-md transition-shadow"
                style={{ borderLeftColor: jobColor(job) }} data-testid={`dispatch-unassigned-card-${job.id}`}>
                <p className="text-sm font-bold text-slate-800 truncate">{job.customer_name || "New Job"}</p>
                <p className="text-xs text-slate-500 truncate">{job.job_type || "Service Call"}{job.complaint ? ` · ${job.complaint}` : ""}</p>
                <p className="text-[11px] text-slate-400 truncate">{job.job_number}</p>
              </div>
            ))}
          </div>
        )}
      </div>
      {loading ? (
        <div className="flex items-center justify-center py-24 text-slate-400"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : view === "day" ? (
        <DayGrid date={date} dateISO={dateISO} dayJobsByTech={dayJobsByTech} overlaps={overlaps} techRows={techRows}
          trackRefs={trackRefs} onDragStart={onDragStart} onDragEnd={onDragEnd} dropOnDayTrack={dropOnDayTrack}
          onEmptyClick={(tech, min) => setCreateSlot({ tech, dateISO, time: minToHHMM(min) })} />
      ) : (
        <WeekGrid weekDays={weekDays} active={active} isRow={isRow} techRows={techRows}
          onDragStart={onDragStart} onDragEnd={onDragEnd} dropOnWeekCell={dropOnWeekCell}
          onEmptyClick={(tech, iso) => setCreateSlot({ tech, dateISO: iso, time: "09:00" })} />
      )}
      {saving && <div className="fixed bottom-4 right-4 flex items-center gap-2 bg-[#12355B] text-white text-xs px-3 py-2 rounded-lg shadow-lg z-40"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Saving…</div>}
      {createSlot && <QuickCreate slot={createSlot} onClose={() => setCreateSlot(null)} onCreated={() => { setCreateSlot(null); load(); }} />}
    </div>
  );
};

const DayGrid = ({ date, dayJobsByTech, overlaps, trackRefs, onDragStart, onDragEnd, dropOnDayTrack, onEmptyClick, techRows }) => {
  const nowMin = (() => {
    const n = new Date();
    if (toISODate(n) !== toISODate(date)) return null;
    const m = n.getHours() * 60 + n.getMinutes();
    return m >= DAY_START && m <= DAY_END ? m : null;
  })();
  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      <div className="overflow-x-auto">
        <div style={{ minWidth: 150 + TRACK_W }}>
          <div className="flex border-b border-slate-200 bg-slate-50 sticky top-0 z-10">
            <div className="w-[150px] shrink-0 px-3 py-2 text-xs font-bold text-slate-500 border-r border-slate-200">TECHNICIAN</div>
            <div className="relative" style={{ width: TRACK_W }}>
              <div className="flex">
                {SLOTS.map((m) => (
                  <div key={m} className="text-[11px] font-semibold text-slate-400 border-r border-slate-100 py-2 pl-1" style={{ width: SLOT_PX }}>
                    {m % 60 === 0 ? minToLabel(m) : ""}
                  </div>
                ))}
              </div>
            </div>
          </div>
          {techRows.map((tech, ri) => (
            <div key={tech} className={`flex ${ri % 2 ? "bg-white" : "bg-slate-50/40"} border-b border-slate-100`}>
              <div className="w-[150px] shrink-0 px-3 flex items-center gap-2 border-r border-slate-200" style={{ height: ROW_H }}>
                <div className="w-7 h-7 rounded-full bg-[#12355B] text-white text-xs font-bold flex items-center justify-center">{tech.split(" ").map((w) => w[0]).join("").slice(0, 2)}</div>
                <span className="text-sm font-semibold text-slate-800 truncate">{tech}</span>
              </div>
              <div ref={(el) => (trackRefs.current[tech] = el)} className="relative" style={{ width: TRACK_W, height: ROW_H }}
                onDragOver={(e) => e.preventDefault()} onDrop={(e) => dropOnDayTrack(e, tech)}
                onClick={(e) => {
                  const rect = e.currentTarget.getBoundingClientRect();
                  const slot = Math.floor((e.clientX - rect.left) / SLOT_PX);
                  onEmptyClick(tech, DAY_START + Math.max(0, Math.min(SLOTS.length - 1, slot)) * SLOT_MIN);
                }}>
                {SLOTS.map((m, i) => (
                  <div key={m} className={`absolute top-0 bottom-0 ${m % 60 === 0 ? "border-l border-slate-200" : "border-l border-slate-100"}`} style={{ left: i * SLOT_PX, width: SLOT_PX }} />
                ))}
                {nowMin != null && <div className="absolute top-0 bottom-0 w-[2px] bg-[#F97316] z-20" style={{ left: ((nowMin - DAY_START) / SLOT_MIN) * SLOT_PX }} />}
                {dayJobsByTech[tech].map((job) => {
                  const start = parseTime(job.scheduled_time);
                  const dur = job.duration_min || DEFAULT_DURATION;
                  const left = ((start - DAY_START) / SLOT_MIN) * SLOT_PX;
                  const width = Math.max(SLOT_PX - 6, (dur / SLOT_MIN) * SLOT_PX - 6);
                  const bad = overlaps.has(job.id);
                  return (
                    <div key={job.id} draggable onDragStart={(e) => onDragStart(e, job.id)} onDragEnd={onDragEnd} onClick={(ev) => ev.stopPropagation()}
                      className={`absolute top-1.5 rounded-lg px-2 py-1 cursor-grab active:cursor-grabbing overflow-hidden shadow-sm border ${bad ? "border-red-400 ring-1 ring-red-300" : "border-transparent"}`}
                      style={{ left: left + 3, width, height: ROW_H - 12, backgroundColor: `${jobColor(job)}1a`, borderLeft: `4px solid ${jobColor(job)}` }}
                      title={`${job.customer_name} — ${minToLabel(start)} (${dur}m)`} data-testid={`dispatch-job-${job.id}`}>
                      <p className="text-[12px] font-bold text-slate-800 truncate leading-tight">{job.customer_name || "Job"}</p>
                      <p className="text-[10px] text-slate-500 truncate leading-tight flex items-center gap-1"><Clock className="w-2.5 h-2.5" />{minToLabel(start)} · {dur}m</p>
                      {width > 120 && <p className="text-[10px] text-slate-400 truncate leading-tight">{job.job_type || job.complaint}</p>}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const WeekGrid = ({ weekDays, active, isRow, onDragStart, onDragEnd, dropOnWeekCell, onEmptyClick, techRows }) => (
  <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
    <div className="overflow-x-auto">
      <div style={{ minWidth: 150 + 7 * 150 }}>
        <div className="flex border-b border-slate-200 bg-slate-50">
          <div className="w-[150px] shrink-0 px-3 py-2 text-xs font-bold text-slate-500 border-r border-slate-200">TECHNICIAN</div>
          {weekDays.map((d) => (
            <div key={toISODate(d)} className="flex-1 min-w-[150px] px-3 py-2 text-xs font-bold text-slate-500 border-r border-slate-100 text-center">
              {d.toLocaleDateString("en-US", { weekday: "short" })} <span className="text-slate-400">{d.getDate()}</span>
            </div>
          ))}
        </div>
        {techRows.map((tech, ri) => (
          <div key={tech} className={`flex ${ri % 2 ? "bg-white" : "bg-slate-50/40"} border-b border-slate-100`}>
            <div className="w-[150px] shrink-0 px-3 py-2 flex items-center gap-2 border-r border-slate-200">
              <div className="w-7 h-7 rounded-full bg-[#12355B] text-white text-xs font-bold flex items-center justify-center">{tech.split(" ").map((w) => w[0]).join("").slice(0, 2)}</div>
              <span className="text-sm font-semibold text-slate-800 truncate">{tech}</span>
            </div>
            {weekDays.map((d) => {
              const iso = toISODate(d);
              const cell = active.filter((j) => isRow(j.assigned_tech) && j.assigned_tech.trim() === tech && parseTime(j.scheduled_time) != null && j.scheduled_date === iso)
                .sort((a, b) => parseTime(a.scheduled_time) - parseTime(b.scheduled_time));
              return (
                <div key={iso} className="flex-1 min-w-[150px] border-r border-slate-100 p-1.5 space-y-1.5 min-h-[64px]"
                  onDragOver={(e) => e.preventDefault()} onDrop={(e) => dropOnWeekCell(e, tech, iso)}
                  onClick={() => onEmptyClick(tech, iso)} data-testid={`dispatch-week-cell-${tech}-${iso}`}>
                  {cell.map((job) => (
                    <div key={job.id} draggable onDragStart={(e) => onDragStart(e, job.id)} onDragEnd={onDragEnd} onClick={(ev) => ev.stopPropagation()}
                      className="cursor-grab active:cursor-grabbing rounded-md px-2 py-1 shadow-sm overflow-hidden"
                      style={{ backgroundColor: `${jobColor(job)}1a`, borderLeft: `3px solid ${jobColor(job)}` }} data-testid={`dispatch-week-job-${job.id}`}>
                      <p className="text-[11px] font-bold text-slate-800 truncate leading-tight">{minToLabel(parseTime(job.scheduled_time))} {job.customer_name}</p>
                      <p className="text-[10px] text-slate-500 truncate leading-tight">{job.job_type || job.complaint}</p>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  </div>
);

const QuickCreate = ({ slot, onClose, onCreated }) => {
  const [form, setForm] = useState({ customer_name: "", phone: "", address: "", job_type: "Service Call", priority: "Standard", complaint: "", duration_min: 60 });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async () => {
    if (!form.customer_name.trim()) { toast.error("Customer name is required."); return; }
    setBusy(true);
    try {
      await createServiceJob({ ...form, duration_min: Number(form.duration_min) || 60, assigned_tech: slot.tech, scheduled_date: slot.dateISO, scheduled_time: slot.time, actor_role: "DISPATCHER", actor_name: "Dispatch Board" });
      toast.success("Job scheduled.");
      onCreated();
    } catch { toast.error("Could not create job."); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="dispatch-quickcreate-modal">
      <div className="bg-white rounded-2xl w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-lg font-bold text-slate-900">New Job</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-slate-100" data-testid="dispatch-quickcreate-close"><X className="w-5 h-5 text-slate-500" /></button>
        </div>
        <p className="text-sm text-slate-500 mb-4 flex items-center gap-2">
          <User className="w-4 h-4" /> {slot.tech}
          <Clock className="w-4 h-4 ml-2" /> {minToLabel(parseTime(slot.time))} · {new Date(slot.dateISO + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })}
        </p>
        <div className="space-y-3">
          <input autoFocus value={form.customer_name} onChange={(e) => set("customer_name", e.target.value)} placeholder="Customer name *" className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" data-testid="dispatch-qc-name" />
          <div className="grid grid-cols-2 gap-3">
            <input value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="Phone" className="px-3 py-2 rounded-lg border border-slate-200 text-sm" data-testid="dispatch-qc-phone" />
            <input type="number" min="30" step="30" value={form.duration_min} onChange={(e) => set("duration_min", e.target.value)} placeholder="Duration (min)" className="px-3 py-2 rounded-lg border border-slate-200 text-sm" data-testid="dispatch-qc-duration" />
          </div>
          <input value={form.address} onChange={(e) => set("address", e.target.value)} placeholder="Service address" className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" data-testid="dispatch-qc-address" />
          <div className="grid grid-cols-2 gap-3">
            <select value={form.job_type} onChange={(e) => set("job_type", e.target.value)} className="px-3 py-2 rounded-lg border border-slate-200 text-sm" data-testid="dispatch-qc-type">
              {["Service Call", "Maintenance", "Install", "Estimate", "Warranty", "Inspection"].map((t) => <option key={t}>{t}</option>)}
            </select>
            <select value={form.priority} onChange={(e) => set("priority", e.target.value)} className="px-3 py-2 rounded-lg border border-slate-200 text-sm" data-testid="dispatch-qc-priority">
              {["Standard", "High", "Emergency", "Low"].map((t) => <option key={t}>{t}</option>)}
            </select>
          </div>
          <textarea value={form.complaint} onChange={(e) => set("complaint", e.target.value)} placeholder="Complaint / notes" rows={2} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" data-testid="dispatch-qc-complaint" />
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-200 text-sm font-semibold text-slate-600">Cancel</button>
          <button onClick={submit} disabled={busy} className="px-4 py-2 rounded-lg bg-[#F97316] text-white text-sm font-bold flex items-center gap-2 disabled:opacity-60" data-testid="dispatch-qc-submit">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Schedule Job
          </button>
        </div>
      </div>
    </div>
  );
};

export default DispatchBoard;
